import { useInfiniteQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { CONVERSATION_TYPES } from 'store/smsDashboard';

import type {
  ConversationType,
  IConversationsListRes,
} from 'infrastructure/interfaces';

interface Props {
  type: ConversationType;
  cursor?: Date;
  read: string;
  closed: string;
  lastMessageDate: string;
  searchQuery: string;
}

const PAGE_SIZE = 10;
const API_CONVERSATIONS_KEYWORD = 'core-2/conversations/search-keyword';
const API_UNREGISTERED_CONVERSATIONS_KEYWORD =
  'core-2/conversations/search-keyword-unregistered';

export const getSmsDashboardConversationsByKeywordFn = async (props: Props) => {
  const isUnregisteredConversationType =
    props.type === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    props.type === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const url = isUnregisteredConversationType
    ? API_UNREGISTERED_CONVERSATIONS_KEYWORD
    : API_CONVERSATIONS_KEYWORD;

  const { data, error } = await dataService.getList<IConversationsListRes>(
    apiUrlGenerator(url, { ...props, pageSize: PAGE_SIZE }),
  );

  if (error) {
    showResult('Unable to retrieve conversations by keyword');
    throw new Error(error);
  }
  return data;
};

export const getSmsDashboardConversationsByKeywordKey = ({
  type,
  read,
  closed,
  lastMessageDate,
  searchQuery,
}: {
  type: ConversationType;
  read: string;
  closed: string;
  lastMessageDate: string;
  searchQuery: string;
}) => [
  'getSmsDashboardConversationsByKeyword',
  type,
  read,
  closed,
  lastMessageDate,
  searchQuery,
];

export const useSmsDashboardConversationsByKeyword = (props: Props) => {
  const {
    data: conversationsByKeyword,
    refetch,
    isFetching,
    isLoading,
    isRefetching,
    isError,
    fetchNextPage,
    ...other
  } = useInfiniteQuery({
    queryKey: getSmsDashboardConversationsByKeywordKey({
      type: props.type,
      read: props.read,
      closed: props.closed,
      lastMessageDate: props.lastMessageDate,
      searchQuery: props.searchQuery,
    }),
    queryFn: async ({ pageParam = 0 }) =>
      getSmsDashboardConversationsByKeywordFn({
        ...props,
        cursor:
          pageParam ||
          (props.lastMessageDate === 'oldest' ? undefined : new Date()),
      }),
    getNextPageParam: (lastPage) => {
      return lastPage?.hasMore
        ? lastPage.items[lastPage.items.length - 1]?.lastMessageDate
        : false;
    },
    enabled: !!props.searchQuery,
  });

  return {
    conversationsByKeyword: conversationsByKeyword?.pages.flat(),
    conversationsByKeywordTotalCount:
      conversationsByKeyword?.pages.flat()?.[0]?.totalCount || 0,
    fetchConversationsByKeyword: refetch,
    conversationsByKeywordLoading: isLoading || isFetching || isRefetching,
    isLoadingConversationsByKeyword: isLoading,
    isFetching,
    isErrorConversationsByKeyword: isError,
    fetchNextPageConversationsByKeyword: fetchNextPage,
    ...other,
  };
};
